import React, { useState, useEffect } from 'react'
import { Typography, Box, Button, CircularProgress, LinearProgress, useMediaQuery, useTheme } from '@mui/material'
import { QRCodeSVG } from 'qrcode.react'
import { IconCopy, IconShare } from '@tabler/icons-react'
import { useAppSelector, useAppDispatch } from '../../../../store'
import { selectEater, setOpen, setMessage, setSeverity, sendPayoutLinkSms } from '../../../../store/profileSlice'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { ROUTES } from '../../../../helpers/routes-helper'
import { fetchWalletBalance, fetchRewardsCatalog, WalletBalanceResponse, RewardItem } from '../../../../services/rewardsService'
import { payInWallet } from '../../../../helpers/wallet-helper'
import BasicModal from '../../../../components/modal/BasicModal'
import { errorLog } from '../../../../utils/log-helper'
import { SANS_FONT } from '../../../../theme'
import { mapSubscriptionPlanIdToTier } from '../../../../helpers/tier-helper'
import { getTierConfig } from '../../../../config/subscription-tiers'
import {
  WalletContainer,
  VisaCardContainer,
  CardHeader,
  VisaLogo,
  CardNumber,
  BalanceOnCard,
  BalanceLabel,
  BalanceAmount,
  BalanceSubtext,
  PayButton,
  PayButtonText,
} from '../styled/ProfileWallet.styled'

const ProfileWallet: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const eater = useAppSelector(selectEater)
  const dispatch = useAppDispatch()
  const { navigate } = useAppUtilities()
  const [walletBalance, setWalletBalance] = useState<WalletBalanceResponse | null>(null)
  const [isCashOutModalOpen, setIsCashOutModalOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [paymentLinkUrl, setPaymentLinkUrl] = useState<string | null>(null)
  const [isCopyingLink, setIsCopyingLink] = useState(false)
  const [selectedVisaReward, setSelectedVisaReward] = useState<RewardItem | null>(null)
  
  // Fetch VISA rewards from catalog
  useEffect(() => {
    const loadVisaRewards = async (): Promise<void> => {
      try {
        const catalog = await fetchRewardsCatalog()
        // Filter for VISA rewards (cashLike: true, type: VISA)
        const visaItems = catalog.rewards.filter(
          (reward) => reward.cashLike && reward.type?.toUpperCase().includes('VISA')
        )
        // Auto-select first VISA reward if available
        setSelectedVisaReward(visaItems[0] ?? null)
      } catch (error) {
        errorLog('Error loading VISA rewards', error)
      }
    }
    void loadVisaRewards()
  }, [])

  // Fetch wallet balance on mount
  useEffect(() => {
    fetchWalletBalance()
      .then(setWalletBalance)
      .catch(() => {
        // Fallback to empty balance if fetch fails
        setWalletBalance({
          walletAvailableCents: 0,
          monthlyLimitCents: 0,
          alreadyRedeemedCents: 0,
        })
      })
  }, [eater?.points])

  // Calculate credits from wallet balance
  const creditsCents = walletBalance?.walletAvailableCents ?? 0
  const creditsDollars = (creditsCents / 100).toFixed(2)

  // Monthly qualifier — the reward mechanic is monthly, so this month's progress toward the tier
  // goal is a first-class element here (mirrors the points dashboard hero), not a buried optional
  // line. Redeemable credit above is the secondary "ready to redeem" detail.
  const currentPlan =
    !eater?.subscribed || !eater?.subscriptionPlanId
      ? null
      : mapSubscriptionPlanIdToTier(eater.subscriptionPlanId)
  const currentTier = getTierConfig(currentPlan)
  const monthlyEarned = walletBalance?.monthlyProgressPoints ?? 0
  const monthlyGoal = walletBalance?.monthlyGoalPoints ?? currentTier.monthlyGoalPoints
  const monthlyPct = monthlyGoal > 0 ? Math.min(1, monthlyEarned / monthlyGoal) : 0
  const monthlyProgressPercentage = Math.round(monthlyPct * 100)
  const monthlyRemaining = Math.max(0, monthlyGoal - monthlyEarned)
  const monthlyQualified = monthlyGoal > 0 && monthlyEarned >= monthlyGoal

  const cardNumber = '•••• •••• •••• 4532'

  // Determine button label based on subscription status
  const isSubscribed = eater?.subscribed ?? false
  const primaryButtonLabel = isSubscribed
    ? 'View rewards'
    : 'Unlock rewards faster'

  const handlePrimaryClick = (): void => {
    navigate(ROUTES.REDEEM_POINTS)
  }

  // Cash-out (pay-in to a Virtual Visa) requires an active subscription. The backend flags this on
  // the catalog item via canRedeem=false + disableReason SUBSCRIPTION_REQUIRED. Gift-card redeem is
  // unaffected. Credits remain a secondary gate for subscribers.
  const subscriptionRequired = selectedVisaReward?.disableReason === 'SUBSCRIPTION_REQUIRED'
  const selectedVisaAmountCents = selectedVisaReward?.amountCents ?? 0
  const hasEnoughCredits = selectedVisaReward ? creditsCents >= selectedVisaAmountCents : false
  const isCashOutButtonDisabled = subscriptionRequired
    ? false
    : !hasEnoughCredits || isProcessing || !selectedVisaReward

  const handleCashOutClick = (): void => {
    if (subscriptionRequired) {
      // Non-subscribers can't cash out — send them to upgrade instead of opening the modal.
      navigate(ROUTES.REDEEM_POINTS)
      return
    }
    setIsCashOutModalOpen(true)
  }

  const handleCashOutConfirm = async (): Promise<void> => {
    if (!selectedVisaReward) return
    
    setIsProcessing(true)
    try {
      // Call payInWallet with rewardCode only (no product IDs, no Tremendous product IDs)
      const response = await payInWallet(selectedVisaReward.code)
      
      if (response.paymentLinkUrl) {
        setPaymentLinkUrl(response.paymentLinkUrl)
        setIsProcessing(false)
        // Don't auto-redirect - let user click "Open claim link" button
      } else {
        throw new Error('No payment link URL in response')
      }
    } catch (error) {
      errorLog('Error initiating cash out to Visa', error)
      dispatch(setOpen(true))
      dispatch(setMessage('Failed to process cash out. Please try again.'))
      dispatch(setSeverity('error'))
      setIsProcessing(false)
    }
  }

  const handleTextMeLink = async (): Promise<void> => {
    if (!paymentLinkUrl) return
    
    if (!eater?.phone) {
      dispatch(setOpen(true))
      dispatch(setMessage('Add a phone number to enable SMS'))
      dispatch(setSeverity('info'))
      return
    }
    
    try {
      const result = await dispatch(sendPayoutLinkSms({ paymentLinkUrl }))
      if (sendPayoutLinkSms.fulfilled.match(result)) {
        dispatch(setOpen(true))
        dispatch(setMessage('Sent to your phone'))
        dispatch(setSeverity('success'))
      } else {
        throw new Error('Failed to send SMS')
      }
    } catch (error) {
      errorLog('Error sending SMS', error)
      dispatch(setOpen(true))
      dispatch(setMessage('Failed to send SMS. Please try again.'))
      dispatch(setSeverity('error'))
    }
  }

  const handleCopyClaimLink = async (): Promise<void> => {
    if (!paymentLinkUrl) return
    
    setIsCopyingLink(true)
    try {
      await navigator.clipboard.writeText(paymentLinkUrl)
      dispatch(setOpen(true))
      dispatch(setMessage('Claim link copied to clipboard'))
      dispatch(setSeverity('success'))
    } catch (error) {
      errorLog('Error copying link', error)
      dispatch(setOpen(true))
      dispatch(setMessage('Failed to copy link'))
      dispatch(setSeverity('error'))
    } finally {
      setIsCopyingLink(false)
    }
  }

  const shareClaimLink = async (url: string): Promise<void> => {
    if (!url) return
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Resactly Rewards',
          text: 'Open your rewards claim link:',
          url,
        })
        dispatch(setOpen(true))
        dispatch(setMessage('Share sheet opened'))
        dispatch(setSeverity('success'))
      } else {
        // Fallback to clipboard if Web Share API not available
        await navigator.clipboard.writeText(url)
        dispatch(setOpen(true))
        dispatch(setMessage('Claim link copied'))
        dispatch(setSeverity('success'))
      }
    } catch (error: any) {
      // If user cancels share sheet, keep it quiet (no scary error)
      // Only show error if it's not a user cancellation (AbortError)
      if (error?.name !== 'AbortError') {
        errorLog('Error sharing link', error)
        dispatch(setOpen(true))
        dispatch(setMessage('Could not share link'))
        dispatch(setSeverity('error'))
      }
    }
  }

  const handleOpenClaimLink = (): void => {
    if (paymentLinkUrl) {
      window.location.href = paymentLinkUrl
    }
  }

  const handleCashOutCancel = (): void => {
    setIsCashOutModalOpen(false)
    setPaymentLinkUrl(null)
  }

  return (
    <WalletContainer>
      <VisaCardContainer>
        <CardHeader>
          <VisaLogo>VISA</VisaLogo>
        </CardHeader>

        <CardNumber>{cardNumber}</CardNumber>

        <BalanceOnCard>
          {/* Primary: this month's qualifier progress — the number that unlocks the credit. */}
          <BalanceLabel>{currentTier.name} · This month&apos;s credit</BalanceLabel>
          <Typography
            sx={{
              color: '#FFF',
              fontFamily: SANS_FONT,
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '28px',
              mt: 0.25,
            }}
          >
            {monthlyEarned.toLocaleString()}{' '}
            <Box
              component="span"
              sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '15px', fontWeight: 500 }}
            >
              of {monthlyGoal.toLocaleString()} points
            </Box>
          </Typography>
          <LinearProgress
            variant="determinate"
            value={monthlyProgressPercentage}
            sx={{
              mt: 1,
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              '& .MuiLinearProgress-bar': { borderRadius: 3, backgroundColor: '#FFF' },
            }}
          />
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontFamily: SANS_FONT,
              fontSize: '13px',
              fontWeight: 500,
              lineHeight: '18px',
              mt: 1,
            }}
          >
            {monthlyQualified
              ? "You've hit this month's credit 🎉"
              : `Earn ${monthlyRemaining.toLocaleString()} more this month to unlock your credit.`}
          </Typography>

          {/* Secondary: redeemable credit — banked and ready to redeem. */}
          <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
            <BalanceLabel>Ready to redeem</BalanceLabel>
            <BalanceAmount>${creditsDollars}</BalanceAmount>
            <BalanceSubtext>Credits are subject to availability.</BalanceSubtext>
          </Box>
        </BalanceOnCard>
      </VisaCardContainer>

      <PayButton onClick={handlePrimaryClick}>
        <PayButtonText>{primaryButtonLabel}</PayButtonText>
      </PayButton>

      {/* Cash out to Visa button */}
      <Button
        variant="outlined"
        fullWidth
        onClick={handleCashOutClick}
        disabled={isCashOutButtonDisabled}
        sx={{
          mt: 2,
          py: 1.5,
          borderColor: 'primary.main',
          color: 'primary.main',
          '&:hover': {
            borderColor: 'primary.dark',
            backgroundColor: 'primary.50',
          },
          '&:disabled': {
            borderColor: 'grey.300',
            color: 'grey.500',
          },
        }}
      >
        {isProcessing ? (
          <CircularProgress size={20} sx={{ color: 'primary.main' }} />
        ) : subscriptionRequired ? (
          'Upgrade to cash out'
        ) : (
          'Cash out to Visa'
        )}
      </Button>
      {subscriptionRequired && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
          Cash out with Explorer or VIP. Gift cards are available on every plan.
        </Typography>
      )}

      {/* Cash-out ready modal */}
      <BasicModal
        open={isCashOutModalOpen}
        handleClose={handleCashOutCancel}
        title={paymentLinkUrl ? 'Cash-out ready' : 'Cash out to Virtual Visa'}
        buttonName={paymentLinkUrl ? 'Open claim link' : 'Continue'}
        cancelButtonName="Cancel"
        onConfirm={paymentLinkUrl ? handleOpenClaimLink : handleCashOutConfirm}
        onCancel={handleCashOutCancel}
        saveBtnDisabled={isProcessing || (!paymentLinkUrl && (!selectedVisaReward || isProcessing))}
        isLoader={isProcessing}
        modelWidth="480px"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2 }}>
          {!paymentLinkUrl ? (
            <>
              <Typography variant="body1" color="text.primary">
                Cash out {selectedVisaReward ? `$${(selectedVisaReward.amountCents / 100).toFixed(2)}` : ''} to a Virtual Visa card.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your virtual Visa will be delivered via a claim link.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600, textAlign: 'center' }}>
                Your virtual Visa is ready
              </Typography>
              
              {/* Desktop: Show QR code, Mobile: Show copy button */}
              {!isMobile ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    p: 3,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                  }}
                >
                  <QRCodeSVG
                    value={paymentLinkUrl}
                    size={180}
                    level="M"
                    includeMargin={true}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                    Scan QR code to open claim link
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => { void handleCopyClaimLink() }}
                    disabled={isCopyingLink}
                    startIcon={<IconCopy size={18} />}
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    }}
                  >
                    {isCopyingLink ? 'Copying...' : 'Copy claim link'}
                  </Button>
                  {paymentLinkUrl && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        textAlign: 'center',
                        wordBreak: 'break-all',
                        fontSize: '0.7rem',
                      }}
                    >
                      {paymentLinkUrl.length > 50
                        ? `${paymentLinkUrl.substring(0, 50)}...`
                        : paymentLinkUrl}
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                  Steps:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  1) Open link
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  2) Activate card
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  3) Add to Apple Pay / Google Wallet (manual)
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                You&apos;ll complete activation on the next page. You can add the card to Apple Pay / Google Wallet by entering the card details.
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                Credits are not cash and are subject to availability.
              </Typography>

              {/* Action buttons */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                {/* Share link button - show on both mobile and desktop */}
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => { void shareClaimLink(paymentLinkUrl) }}
                  startIcon={<IconShare size={18} />}
                  sx={{
                    borderColor: 'grey.300',
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: 'grey.400',
                      backgroundColor: 'grey.50',
                    },
                  }}
                >
                  Share link
                </Button>

                {/* Copy claim link button - desktop only (mobile already has it in QR section) */}
                {!isMobile && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => { void handleCopyClaimLink() }}
                    disabled={isCopyingLink}
                    startIcon={<IconCopy size={18} />}
                    sx={{
                      borderColor: 'grey.300',
                      color: 'text.secondary',
                      '&:hover': {
                        borderColor: 'grey.400',
                        backgroundColor: 'grey.50',
                      },
                    }}
                  >
                    {isCopyingLink ? 'Copying...' : 'Copy claim link'}
                  </Button>
                )}

                {/* Text me the link button - show if phone exists */}
                {eater?.phone && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => { void handleTextMeLink() }}
                    sx={{
                      borderColor: 'grey.300',
                      color: 'text.secondary',
                      '&:hover': {
                        borderColor: 'grey.400',
                        backgroundColor: 'grey.50',
                      },
                    }}
                  >
                    Text me the link
                  </Button>
                )}
              </Box>
            </>
          )}
        </Box>
      </BasicModal>
    </WalletContainer>
  )
}

export default ProfileWallet
